import type { SceneManager } from "./SceneManager.js";

/**
 * GF5 — "tube TV" presentation mode.
 *
 * Crisp native rendering exposes how simple the assets are; this mode
 * leans the other way: the scene renders at a chunky internal resolution
 * and upscales with hard pixels, under a scanline + vignette overlay.
 * Pure presentation — no gameplay reads it. Persisted in localStorage
 * and boot-overridable with `?retro=1` / `?retro=0`.
 */

const STORAGE_KEY = "bk:retro";
const RETRO_SCALE = 0.34;

let overlay: HTMLDivElement | null = null;
let managed: { sceneManager: SceneManager; canvas: HTMLCanvasElement } | null = null;
let enabled = false;

export function isRetroEnabled(): boolean {
  return enabled;
}

/** Read the persisted flag, with an optional URL override. */
export function loadRetroPreference(search: string): boolean {
  const params = new URLSearchParams(search);
  const fromUrl = params.get("retro");
  if (fromUrl !== null) return fromUrl !== "0" && fromUrl !== "false";
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Bind the mode to the live renderer + canvas, applying the initial state. */
export function initRetroPresentation(
  sceneManager: SceneManager,
  canvas: HTMLCanvasElement,
  initial: boolean,
): void {
  managed = { sceneManager, canvas };
  applyRetro(initial, false);
}

/** Toggle at runtime (Settings). Persists unless `persist` is false. */
export function applyRetro(on: boolean, persist = true): void {
  enabled = on;
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
    } catch {
      // storage unavailable — session-only toggle
    }
  }
  if (!managed) return;
  const { sceneManager, canvas } = managed;
  sceneManager.setResolutionScale(on ? RETRO_SCALE : 1);
  canvas.style.imageRendering = on ? "pixelated" : "";
  if (on) {
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "retro-overlay";
      Object.assign(overlay.style, {
        position: "fixed",
        inset: "0",
        pointerEvents: "none",
        zIndex: "5", // above the canvas, below #ui-root
        background:
          "radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(0,0,0,0.42) 100%)",
      });
      const scanlines = document.createElement("div");
      Object.assign(scanlines.style, {
        position: "absolute",
        inset: "0",
        background:
          "repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(0,0,0,0.16) 3px)",
        mixBlendMode: "multiply",
      });
      overlay.appendChild(scanlines);
      canvas.parentElement?.appendChild(overlay);
    }
    overlay.style.display = "";
  } else if (overlay) {
    overlay.style.display = "none";
  }
}
